import React from 'react';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Asset } from 'config/api/assets/assets.types';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import assets_messages from 'messages/assets_messages';
import print_template_messages from 'messages/print_template_messages';

import { renderPrintableValue } from '../../utils/printText';

const PRINT_STYLES = `
.ps-assets-list-print { color: #111; font-family: Arial, sans-serif; font-size: 11px; line-height: 1.35; padding: 20px; }
.ps-assets-list-print__header { border-bottom: 1px solid #333; margin-bottom: 14px; padding-bottom: 8px; }
.ps-assets-list-print__title { font-size: 22px; margin: 0 0 6px; }
.ps-assets-list-print__meta { color: #555; margin: 0; }
.ps-assets-list-print__table { border-collapse: collapse; table-layout: fixed; width: 100%; }
.ps-assets-list-print__table th, .ps-assets-list-print__table td { border: 1px solid #cfcfcf; padding: 6px; text-align: left; vertical-align: top; word-break: break-word; }
.ps-assets-list-print__table th { background: #f2f2f2; font-weight: 700; }
.ps-assets-list-print__table thead { display: table-header-group; }
.ps-assets-list-print__table tr { break-inside: avoid; page-break-inside: avoid; }
.ps-assets-list-print__empty { border: 1px solid #d5d5d5; padding: 12px; }
@page { size: A4 landscape; }
@media print {
  .ps-assets-list-print { padding: 0; }
  .ps-assets-list-print__table thead { display: table-header-group; }
  .ps-assets-list-print__table tr { break-inside: avoid; page-break-inside: avoid; }
}
`;

type Props = {
  assets: Asset[];
  printedAt?: Date;
};

const getOwnerName = (asset: Asset): string =>
  renderPrintableValue(
    asset.owner.profile.fullName || `${asset.owner.profile.firstName} ${asset.owner.profile.lastName}`.trim() || asset.owner.email,
  );

const AssetsListPrintTemplate: React.FC<Props> = ({ assets, printedAt = new Date() }) => {
  const { t } = useTranslation();

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <article className='ps-assets-list-print'>
        <header className='ps-assets-list-print__header'>
          <h1 className='ps-assets-list-print__title'>{t(assets_messages.print.assets_title)}</h1>
          <p className='ps-assets-list-print__meta'>
            {t(print_template_messages.footer.printed_on)}: {format(printedAt, 'yyyy-MM-dd HH:mm')}
          </p>
        </header>
        {assets.length ? (
          <table className='ps-assets-list-print__table'>
            <thead>
              <tr>
                <th style={{ width: '20%' }}>{t(assets_messages.assets_table.name)}</th>
                <th style={{ width: '20%' }}>{t(assets_messages.assets_table.owner)}</th>
                <th style={{ width: '20%' }}>{t(assets_messages.assets_table.category)}</th>
                <th style={{ width: '40%' }}>{t(assets_messages.assets_table.description)}</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(asset => (
                <tr key={asset.id}>
                  <td>{renderPrintableValue(asset.name)}</td>
                  <td>{getOwnerName(asset)}</td>
                  <td>{renderPrintableValue(translatedOrFallback(t, asset.category.nameTranslationKey, asset.category.name))}</td>
                  <td>{renderPrintableValue(asset.info.assetDescription)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className='ps-assets-list-print__empty'>{t(assets_messages.print.empty_assets)}</p>
        )}
      </article>
    </>
  );
};

export default AssetsListPrintTemplate;
